import type { AntSimWorld, FoodField } from './contracts';
import type { Terrain } from '../game/Terrain';
import { resolveObstacles, type Obstacle } from './Collision';

export interface GrasshopperState {
  x: number;
  y: number;
  z: number;
  heading: number;
  speed: number;
  hp: number;
  alive: boolean;
  task: 'graze' | 'flee' | 'hop';
  respawnT: number;
  hopCooldown: number;
  hopT: number;
  hopDuration: number;
  /** Upward lift during a hop, rendered as a Y offset. */
  hopLift: number;
}

export interface GrasshopperSimOptions {
  count?: number;
  patrolRadius?: number;
  spiders?: Array<{ x: number; z: number; alive: boolean }>;
  food?: FoodField;
  obstacles?: readonly Obstacle[];
  nearbyObstacles?: (x: number, z: number) => readonly Obstacle[];
}

const GRAZE_SPEED = 0.08;
const FLEE_SPEED = 0.55;
const FLEE_RADIUS = 2.5;
const HOP_COOLDOWN = 6;
const HOP_DURATION = 0.55;
const HOP_DISTANCE = 1.8;
const MAX_HP = 10.0;
const RESPAWN_DELAY = 20;
const MOB_KILL_COUNT = 20;        // 20 ants within bite range drops it instantly
const BITE_RADIUS = 0.45;
const BITE_RADIUS_SQ = BITE_RADIUS * BITE_RADIUS;
const PER_ANT_DPS = 0.04;         // slow drain per nearby ant (pre-mob-kill)
const SPIDER_REACH = 0.6;
const SPIDER_REACH_SQ = SPIDER_REACH * SPIDER_REACH;
const SPIDER_DPS = 6.0;           // spider finishes it fast

export class GrasshopperSim {
  readonly hoppers: GrasshopperState[] = [];
  private readonly patrolRadius: number;

  private spiders: Array<{ x: number; z: number; alive: boolean }>;
  private food: FoodField | null;
  private readonly obstacles: readonly Obstacle[];
  private readonly nearbyObstacles: ((x: number, z: number) => readonly Obstacle[]) | null;

  constructor(
    private terrain: Terrain,
    private world: AntSimWorld,
    opts: GrasshopperSimOptions = {},
  ) {
    const count = opts.count ?? 2;
    this.patrolRadius = opts.patrolRadius ?? 30;
    this.spiders = opts.spiders ?? [];
    this.food = opts.food ?? null;
    this.obstacles = opts.obstacles ?? [];
    this.nearbyObstacles = opts.nearbyObstacles ?? null;
    for (let i = 0; i < count; i++) this.hoppers.push(this.makeHopper());
  }

  private makeHopper(): GrasshopperState {
    const a = Math.random() * Math.PI * 2;
    const r = 5 + Math.random() * (this.patrolRadius - 5);
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    return {
      x, z,
      y: this.terrain.getHeight(x, z),
      heading: Math.random() * Math.PI * 2,
      speed: 0,
      hp: MAX_HP,
      alive: true,
      task: 'graze',
      respawnT: 0,
      hopCooldown: HOP_COOLDOWN + Math.random() * 3,
      hopT: 0,
      hopDuration: HOP_DURATION,
      hopLift: 0,
    };
  }

  update(dt: number): void {
    const ants = this.world.ants;
    for (let i = 0; i < this.hoppers.length; i++) {
      const g = this.hoppers[i];

      if (!g.alive) {
        g.respawnT -= dt;
        if (g.respawnT <= 0) {
          const fresh = this.makeHopper();
          Object.assign(g, fresh);
        }
        continue;
      }

      // Damage bookkeeping: any ant within BITE_RADIUS chews; 20+ = instant kill.
      // Any spider within SPIDER_REACH eats it fast (6 dps).
      let biters = 0;
      for (let j = 0; j < ants.length; j++) {
        const a = ants[j];
        if (!a.alive || a.nestCycleT > 1e6) continue;
        const ddx = a.x - g.x;
        const ddz = a.z - g.z;
        if (ddx * ddx + ddz * ddz < BITE_RADIUS_SQ) biters++;
      }
      let spiderClose = false;
      for (let j = 0; j < this.spiders.length; j++) {
        const s = this.spiders[j];
        if (!s.alive) continue;
        const sdx = s.x - g.x;
        const sdz = s.z - g.z;
        if (sdx * sdx + sdz * sdz < SPIDER_REACH_SQ) { spiderClose = true; break; }
      }
      if (biters >= MOB_KILL_COUNT) {
        g.hp = 0;
      } else {
        if (biters > 0) g.hp -= biters * PER_ANT_DPS * dt;
        if (spiderClose) g.hp -= SPIDER_DPS * dt;
      }
      if (g.hp <= 0) {
        g.alive = false;
        g.respawnT = RESPAWN_DELAY;
        if (this.food) this.food.spawn(g.x, g.z, 3, 8, 'corpse');
        continue;
      }

      // Check nearest ant — grasshoppers flee if anything (any team) gets close.
      let nearestD2 = Infinity;
      let nearestAntX = 0, nearestAntZ = 0;
      for (let j = 0; j < ants.length; j++) {
        const a = ants[j];
        if (!a.alive || a.nestCycleT > 1e6) continue;
        const dx = a.x - g.x;
        const dz = a.z - g.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < nearestD2) { nearestD2 = d2; nearestAntX = a.x; nearestAntZ = a.z; }
      }

      const fleeTriggered = nearestD2 < FLEE_RADIUS * FLEE_RADIUS;

      // Hop arc drives a temporary upward lift + forward leap. During a hop,
      // the grasshopper commits to its current heading and speed.
      if (g.task === 'hop') {
        g.hopT += dt;
        const t = Math.min(1, g.hopT / g.hopDuration);
        g.hopLift = Math.sin(t * Math.PI) * 0.25;
        g.speed = HOP_DISTANCE / g.hopDuration;
        if (t >= 1) {
          g.task = fleeTriggered ? 'flee' : 'graze';
          g.hopT = 0;
          g.hopLift = 0;
          g.hopCooldown = HOP_COOLDOWN + Math.random() * 3;
        }
      } else if (fleeTriggered) {
        // Turn away from the nearest ant and hop.
        g.heading = Math.atan2(g.z - nearestAntZ, g.x - nearestAntX);
        g.task = 'hop';
        g.hopT = 0;
        g.speed = FLEE_SPEED;
      } else {
        // Idle graze + random hop.
        g.task = 'graze';
        g.speed = GRAZE_SPEED;
        g.hopCooldown -= dt;
        if (g.hopCooldown <= 0) {
          g.heading = Math.random() * Math.PI * 2;
          g.task = 'hop';
          g.hopT = 0;
        }
      }

      g.x += Math.cos(g.heading) * g.speed * dt;
      g.z += Math.sin(g.heading) * g.speed * dt;

      if (this.obstacles.length > 0) resolveObstacles(g, 0.1, this.obstacles);
      if (this.nearbyObstacles) resolveObstacles(g, 0.1, this.nearbyObstacles(g.x, g.z));

      // Stay inside the patrol ring.
      const r = Math.hypot(g.x, g.z);
      if (r > this.patrolRadius) {
        const k = this.patrolRadius / r;
        g.x *= k;
        g.z *= k;
        g.heading = Math.atan2(-g.z, -g.x);
      }

      g.y = this.terrain.getHeight(g.x, g.z) + g.hopLift;
    }
  }

  /** Instant-kill every alive grasshopper within `radius` of (cx, cz). Each
   *  kill drops a corpse food pile. Respawn clock handles regeneration. */
  killArea(cx: number, cz: number, radius: number): number {
    const r2 = radius * radius;
    let killed = 0;
    for (let i = 0; i < this.hoppers.length; i++) {
      const g = this.hoppers[i];
      if (!g.alive) continue;
      const dx = g.x - cx;
      const dz = g.z - cz;
      if (dx * dx + dz * dz > r2) continue;
      g.alive = false;
      g.respawnT = RESPAWN_DELAY;
      if (this.food) this.food.spawn(g.x, g.z, 3, 8, 'corpse');
      killed++;
    }
    return killed;
  }

  /** Kill the nearest alive grasshopper within `range` of (x, z). Returns its
   *  position so the caller can spawn a food corpse. */
  tryKillNear(x: number, z: number, range: number): { x: number; z: number } | null {
    const r2 = range * range;
    for (let i = 0; i < this.hoppers.length; i++) {
      const g = this.hoppers[i];
      if (!g.alive) continue;
      const dx = g.x - x;
      const dz = g.z - z;
      if (dx * dx + dz * dz < r2) {
        g.hp -= 1.0;
        if (g.hp <= 0) {
          g.alive = false;
          g.respawnT = RESPAWN_DELAY;
          return { x: g.x, z: g.z };
        }
        return null;
      }
    }
    return null;
  }
}
